import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { IconDefinition, faBirthdayCake, faClock } from "@outschool/icons";
import { CreditIcon } from "@outschool/ui-components-shared";
import React from "react";

import usePriceDetailsString from "../hooks/usePriceDetailsString";
import { getClassTypeIcon, useSelfPacedIconInfo } from "../icons";
import useActivityString from "../translations/useActivityString";
import { ActivityListingProps } from "..";

export type SearchActivityType = Pick<
  Activity,
  | "uid"
  | "user_uid"
  | "isClub"
  | "is_ongoing_weekly"
  | "size_min"
  | "size_max"
  | "hasTeacherSchedule"
  | "duration_weeks"
  | "weekly_meetings"
  | "duration_minutes"
  | "allows_recurring_payment"
  | "price_cents"
  | "age_min"
  | "age_max"
  | "subject"
  | "canEnroll"
  | "isFlexSchedule"
  | "isSelfPaced"
> &
  Partial<Pick<Activity, "clubSection" | "priceCreditsPerMeeting">>;

interface PriceDetailsProps {
  activity: SearchActivityType;
  variant?: ActivityListingProps["variant"];
  showCredits?: boolean;
}

function IconBullet({
  icon,
  text,
}: {
  icon: IconDefinition;
  text: string | null;
}) {
  const fixLineHeight = text?.search(/Group|1\-on\-1/) !== -1;
  return (
    <Box flex sx={{ textAlign: "left" }}>
      <Icon
        icon={icon}
        sx={{
          width: "1.25em",
          marginRight: "0.5em",
        }}
      />
      <Typography
        sx={{
          lineHeight: fixLineHeight ? "1em" : "inherit",
        }}
        variant={"caption"}
      >
        {text}
      </Typography>
    </Box>
  );
}

export default function ListingDetails({
  activity,
  variant,
  showCredits,
}: PriceDetailsProps) {
  const { ageRangeString } = useActivityString();
  const {
    perClassString,
    classTypeString,
    perMeetingPriceString,
    detailedScheduleString,
  } = usePriceDetailsString();
  const classTypeIcon = getClassTypeIcon(activity);
  const selfPacedIconInfo = useSelfPacedIconInfo(activity);
  const theme = useTheme();

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        width: "100%",
        paddingX: "30px",
        paddingY: "16px",
      }}
    >
      <Box flex sx={{ paddingBottom: "4px" }}>
        {variant !== "Review" && (
          <Box
            flex
            sx={{
              borderBottom: "1px solid #cccccc",
              width: "100%",
              gap: "0.5em",
              paddingRight: "1rem",
            }}
          >
            <Typography variant="display3" emphasized color={"grey.900"}>
              {showCredits ? (
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      paddingTop: "0.5rem",
                      paddingRight: "0.5rem",
                    }}
                  >
                    <CreditIcon
                      fillColor={theme.palette.warning.main}
                      strokeColor={theme.palette.warning[700]}
                      sx={{ weidth: "2.4rem", height: "2.4rem" }}
                    />
                  </Box>
                  {activity.priceCreditsPerMeeting}
                </Box>
              ) : (
                perMeetingPriceString(activity)
              )}
            </Typography>
            <Typography variant="caption" sx={{ alignSelf: "center" }}>
              {perClassString(activity)}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        flex
        sx={{
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        <IconBullet icon={classTypeIcon} text={classTypeString(activity)} />
        <IconBullet icon={faBirthdayCake} text={ageRangeString(activity)} />
        {activity.isSelfPaced ? (
          <IconBullet
            icon={selfPacedIconInfo.weeksOfFeedback.icon}
            text={selfPacedIconInfo.weeksOfFeedback.text}
          />
        ) : (
          <IconBullet icon={faClock} text={detailedScheduleString(activity)} />
        )}
      </Box>
    </Box>
  );
}
